import './registration-person-print.scss';

import { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';

import { useCurrentHealthProvider } from '../../../../data/data/health-providers/use-current-health-provider';
import { getRegistrationAddress } from '../../../../data/data/registrations/utils';
import {
  RegistrationDetailItemDataFragment,
  RegistrationPersonItemDataFragment,
} from '../../../../data/types/api/graphql';
import { formatDate } from '../../../../data/utils/date.utils';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { RegistrationPerson } from '../registration-modal/registration-person';

/** Set on <body> while the print view exists; the print stylesheet keys on it. */
const bodyClassName = 'registration-print-open';

type RegistrationPersonPrintProps = {
  registration: RegistrationDetailItemDataFragment;
  person: RegistrationPersonItemDataFragment;
  /** Called once the browser's print dialog has closed (printed or not). */
  onClose?: () => void;
};

/**
 * The registration form of one person, laid out for paper. It is rendered
 * straight under <body>, outside the app shell and the modal, so the print
 * stylesheet can hide everything else and leave a single A4 page.
 *
 * There is no PDF generation: the browser's print dialog is opened right away
 * and "Save as PDF" takes its file name from document.title, which is set to
 * "Registration <last name> <date>" for as long as this view exists.
 *
 * The BSN is never printed, not even while the registration is still open and
 * the server still hands it out.
 */
export function RegistrationPersonPrint({
  registration,
  person,
  onClose,
}: RegistrationPersonPrintProps) {
  const intl = useIntl();
  const { healthProvider } = useCurrentHealthProvider();

  const submittedAt = useMemo(
    () => formatDate(registration.submittedAt, 'dd-mm-YYYY'),
    [registration.submittedAt]
  );

  const documentTitle = useMemo(
    () =>
      intl.formatMessage(
        { id: 'registrations.print.document-title' },
        { lastName: person.lastName, date: submittedAt }
      ),
    [intl, person.lastName, submittedAt]
  );

  const printablePerson = useMemo(() => ({ ...person, bsn: null }), [person]);

  // Kept in a ref so a new callback from the parent does not re-run the
  // effect below and print a second time.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // StrictMode runs effects twice in development; print only once.
  const printedRef = useRef(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = documentTitle;
    document.body.classList.add(bodyClassName);

    const onAfterPrint = () => onCloseRef.current?.();
    window.addEventListener('afterprint', onAfterPrint);

    if (!printedRef.current) {
      printedRef.current = true;
      window.print();
    }

    return () => {
      window.removeEventListener('afterprint', onAfterPrint);
      document.body.classList.remove(bodyClassName);
      document.title = previousTitle;
    };
  }, [documentTitle]);

  return ReactDOM.createPortal(
    <div
      data-test-id="registration-person-print"
      data-item-id={person.id}
      className="registration-print"
    >
      <h1>
        <CustomFormattedMessage id="registrations.print.title" />
      </h1>

      <div className="registration-print__household">
        <h4>
          <CustomFormattedMessage id="registrations.print.practice" />
        </h4>
        <p>{healthProvider?.name ?? '-'}</p>

        <h4>
          <CustomFormattedMessage id="registrations.submitted-at" />
        </h4>
        <p>{submittedAt}</p>

        <h4>
          <CustomFormattedMessage id="registrations.address" />
        </h4>
        <p>{getRegistrationAddress(registration)}</p>
      </div>

      <RegistrationPerson person={printablePerson} />
    </div>,
    document.body
  );
}
